import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * Diálogo modal sobre `<dialog>` nativo.
 *
 * ## Por que o elemento nativo, e não uma div com `role="dialog"`
 *
 * `showModal()` entrega de graça o que uma implementação manual erra: foco
 * preso dentro do diálogo, `Esc` fechando, o resto da página marcado como
 * inerte para leitor de tela, e a camada de topo acima de qualquer `z-index`.
 * A versão manual desse conjunto tem uns 200 linhas e costuma esquecer o
 * `inert` — e aí quem usa leitor de tela navega pelo formulário de trás.
 *
 * `componentes/ui` não tinha modal de propósito enquanto nenhuma tela escrevia.
 * Agora que há formulários de cadastro, ele deixou de ser enfeite.
 */
export function Modal(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly titulo: string;
  readonly descricao?: string;
  readonly children: ReactNode;
  readonly rodape?: ReactNode;
  /** `ampla`: detalhe com coluna lateral de ações, como o card do CRM. */
  readonly largura?: 'media' | 'larga' | 'ampla';
}): JSX.Element | null {
  const dialogo = useRef<HTMLDialogElement>(null);
  // Um id por modal: um aberto sobre o outro (o card do CRM e o "Copiar para
  // um quadro") não podem dar o mesmo nome aos dois títulos.
  const idDoTitulo = useId();

  useEffect(() => {
    const el = dialogo.current;
    if (el === null) return;

    if (props.aberto && !el.open) el.showModal();
    if (!props.aberto && el.open) el.close();
  }, [props.aberto]);

  useEffect(() => {
    const el = dialogo.current;
    if (el === null) return;

    // `Esc` fecha o `<dialog>` sozinho — sem este ouvinte o React continuaria
    // achando que está aberto, e reabrir exigiria dois cliques.
    const aoCancelar = (evento: Event): void => {
      evento.preventDefault();
      props.aoFechar();
    };
    el.addEventListener('cancel', aoCancelar);
    return () => el.removeEventListener('cancel', aoCancelar);
  }, [props.aoFechar]);

  return (
    <dialog
      ref={dialogo}
      aria-labelledby={idDoTitulo}
      className={`w-[calc(100vw-2rem)] rounded-painel border border-borda bg-superficie-elevada p-0 text-conteudo-primario backdrop:bg-superficie-base/70 backdrop:backdrop-blur-sm ${
        props.largura === 'ampla'
          ? 'max-w-5xl'
          : props.largura === 'larga'
            ? 'max-w-3xl'
            : 'max-w-lg'
      }`}
      // Clique fora fecha. O alvo é o próprio `<dialog>` só quando o clique cai
      // no backdrop — dentro do conteúdo o alvo é um filho.
      onClick={(evento) => {
        if (evento.target === dialogo.current) props.aoFechar();
      }}
    >
      <div className="flex flex-col gap-1 border-b border-borda px-6 py-5">
        <h2 id={idDoTitulo} className="font-display text-lg font-semibold tracking-tight">
          {props.titulo}
        </h2>
        {props.descricao !== undefined && (
          <p className="text-sm text-conteudo-secundario">{props.descricao}</p>
        )}
      </div>

      {/* Sem corpo quando não há conteúdo: uma confirmação só com título e
          botões ficaria com uma faixa vazia entre as duas bordas. */}
      {props.children !== null && props.children !== undefined && (
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">{props.children}</div>
      )}

      {props.rodape !== undefined && (
        <div className="flex flex-wrap justify-end gap-3 border-t border-borda px-6 py-4">
          {props.rodape}
        </div>
      )}
    </dialog>
  );
}
